import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { useNavigate } from "react-router-dom";
import {
  mostraAlertaError,
  mostraAlertaOk,
  mostraAlertaPersonalizada,
} from "../../../componentes/alerts/sweetAlert";
import { AxiosPublico } from "../../../componentes/axios/Axios";
import Card from "../../../componentes/contenedores/Card";
import ModalBootstrap from "../../../componentes/modal/ModalBootstrap";
import Page from "../../../componentes/plantilla/Page";
import Tablas from "../../../componentes/tablas/TablasClientes";
import {
  ImagenProducto,
  PedidosEditar,
  PedidosListarClienteId
} from "../../../configuracion/apiUrls";
import { DireccionConfig } from "../../../configuracion/Empresa";
import { useContextCliente } from "../../../contexto/cliente/ClienteContext";
import { useContextUsuario } from "../../../contexto/usuario/UsuarioContext";
import GestionUbicacion from "../../../componentes/googlemap/GestionUbicacion";
import VerUbicacion from "../../../componentes/googlemap/VerUbicacion";
moment.locale("es");

const formatoMoneda = (valor) =>
  "L " +
  new Intl.NumberFormat("en-HN", { minimumFractionDigits: 2 }).format(
    parseFloat(valor || 0)
  );

const calcularPrecioFinalConTodo = (datosProducto, cantidad) => {
  let precioFinal = parseFloat(datosProducto.precio) || 0;
  let esMayoreoAplicado = false;
  let esPrecioDeLoteBaseAplicado = false;
  let esPrecioDeLoteCantidadAplicado = false;
  let loteId = null;

  const primerLoteActivo = (datosProducto.lotes || []).find(lote => lote.estado === "Disponible" && lote.existencia > 0);

  if (primerLoteActivo) {
    precioFinal = parseFloat(primerLoteActivo.precio) || precioFinal;
    esPrecioDeLoteBaseAplicado = true;
    loteId = primerLoteActivo.id;

    if (Array.isArray(primerLoteActivo.loteprecios) && primerLoteActivo.loteprecios.length > 0) {
      const lotPreciosOrdenados = [...primerLoteActivo.loteprecios].sort((a, b) => b.cantidad - a.cantidad);

      for (const nivelLotePrecio of lotPreciosOrdenados) {
        if (cantidad >= nivelLotePrecio.cantidad) {
          precioFinal = parseFloat(nivelLotePrecio.precio) || precioFinal;
          esPrecioDeLoteCantidadAplicado = true;
          break;
        }
      }
    }
  } else {
    if (datosProducto.productoprecios && datosProducto.productoprecios.length > 0) {
      const nivelesMayoreoOrdenados = [...datosProducto.productoprecios].sort((a, b) => b.cantidad - a.cantidad);
      for (const nivel of nivelesMayoreoOrdenados) {
        if (cantidad >= nivel.cantidad) {
          precioFinal = parseFloat(nivel.precio) || 0;
          esMayoreoAplicado = true;
          break;
        }
      }
    }
  }

  return {
    price: precioFinal.toFixed(2),
    precioOriginalProducto: parseFloat(datosProducto.precio).toFixed(2),
    esMayoreoAplicado,
    esPrecioDeLoteBaseAplicado,
    esPrecioDeLoteCantidadAplicado,
    loteId
  };
};

const EditarPedidos = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { usuario } = useContextUsuario();
  const [pedidos, setPedidos] = useState([]);
  const [pedidoSeleccionado, setPedidoSeleccionado] = useState(null);
  const [productos, setProductos] = useState([]);
  const [detallesEdicion, setDetallesEdicion] = useState([]);
  const [notaNueva, setNotaNueva] = useState("");
  const [modalProductos, setModalProductos] = useState(false);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const { listaProductos = [] } = useContextCliente();
  const refEdicion = useRef(null);
  const topRef = useRef(null);
  const navigate = useNavigate();
  const [productoSearchTerm, setProductoSearchTerm] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("");
  const [pedidosFiltrados, setPedidosFiltrados] = useState([]);
  const [panelAbierto, setPanelAbierto] = useState(false);
  const [seleccionarDireccion, setSeleccionarDireccion] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [nuevaDireccion, setNuevaDireccion] = useState({
    direccion: DireccionConfig.direccion,
    longitud: DireccionConfig.longitud,
    latitud: DireccionConfig.latitud,
  });
  const [direccionesEdicion, setDireccionesEdicion] = useState([]);

  const transformarProductos = (listaProductos) =>
    Array.isArray(listaProductos)
      ? listaProductos.map((p) => {
        return {
          id: p.id,
          nombre: p.nombre,
          descripcion: p.descripcion,
          precio: parseFloat(p.precio).toFixed(2),
          existencia: p.existencia,
          codigo: p.codigo,
          imagen:
            p.productoImagens && p.productoImagens[0]?.imagen
              ? ImagenProducto + p.productoImagens[0].imagen
              : "/productos.jpg",
          productoprecios: p.productoprecios || [],
          detallepromocions: [],
          lotes: p.lotes || [],
        };
      })
      : [];

  const datos = {
    titulo: {
      titulo: "Edicion de pedidos",
      url: "/app/clientes/pedidos/editar",
      tituloUrl: "pedidos",
      nombreUrl: "Ver Pedidos",
    },
  };

  const fetchPedidosCliente = async () => {
    try {
      const response = await AxiosPublico.get(
        `${PedidosListarClienteId}?clienteId=${usuario.datoPersonales.id}`
      );

      if (Array.isArray(response.data)) {
        const transformados = response.data.map((pedido) => ({
          id: pedido.id,
          fecha: moment(pedido.fecha).format("YYYY-MM-DD HH:mm"),
          estado: pedido.estado,
          envio: pedido.envio,
          notas: pedido.notas || "",
          total: pedido.pedidoDetalles?.reduce(
            (sum, item) => {
              const productoDeLista = listaProductos.find(p => p.id === item.producto?.id);
              const productoParaCalculo = productoDeLista || item.producto;
              const { price: precioCalculado } = calcularPrecioFinalConTodo(
                {
                  ...productoParaCalculo,
                  precio: parseFloat(productoParaCalculo?.precio || 0),
                  productoprecios: productoParaCalculo?.productoprecios || [],
                  detallepromocions: [],
                  lotes: productoParaCalculo?.lotes || [],
                },
                item.cantidad
              );
              return sum + parseFloat(precioCalculado) * (item.cantidad || 1);
            },
            0
          ).toFixed(2),
          detalles: pedido.pedidoDetalles?.map((detalle) => {
            const productoDeLista = listaProductos.find(p => p.id === detalle.producto?.id);
            const productoParaCalculo = productoDeLista || detalle.producto;

            const {
              price: precioCalculado,
              esMayoreoAplicado,
              precioOriginalProducto,
              esPrecioDeLoteBaseAplicado,
              esPrecioDeLoteCantidadAplicado,
              loteId
            } = calcularPrecioFinalConTodo(
              {
                ...productoParaCalculo,
                precio: parseFloat(productoParaCalculo?.precio || 0),
                productoprecios: productoParaCalculo?.productoprecios || [],
                detallepromocions: [],
                lotes: productoParaCalculo?.lotes || [],
              },
              detalle.cantidad
            );

            return {
              id: detalle.id,
              productoId: detalle.producto?.id || null,
              nombre: detalle.producto?.nombre || "Producto no especificado",
              cantidad: detalle.cantidad || 1,
              precio: parseFloat(precioCalculado),
              subtotal: (detalle.cantidad * parseFloat(precioCalculado)).toFixed(2),
              esMayoreoAplicado,
              precioOriginalProducto,
              productoprecios: productoParaCalculo?.productoprecios || [],
              detallepromocions: [],
              lotes: productoParaCalculo?.lotes || [],
              esPrecioDeLoteBaseAplicado,
              esPrecioDeLoteCantidadAplicado,
              loteId: detalle.loteId || loteId,
            };
          }),
          direcciones: pedido.PedidoDireccions?.map((direccion) => ({
            id: direccion.id,
            direccion: direccion.direccion,
            longitud: direccion.longitud || null,
            latitud: direccion.latitud || null
          })) || []
        }));
        transformados.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
        setPedidos(transformados);
        setPedidosFiltrados(transformados);
      }
    } catch (error) {
      mostraAlertaError("Error al obtener los pedidos.", "Error");
    }
  };

  useEffect(() => {
    if (seleccionarDireccion) {
      setIsModalOpen(false);
      if (direccionesEdicion.length >= 1) {
        mostraAlertaError("Solo se permite una dirección por pedido.", "Límite alcanzado");
      } else {
        setDireccionesEdicion((prev) => [...prev, nuevaDireccion]);
      }
      setSeleccionarDireccion(false);
    }
  }, [seleccionarDireccion, nuevaDireccion, direccionesEdicion]);

  const handleFiltrar = () => {
    let filtrados = [...pedidos];

    if (estadoFiltro) {
      filtrados = filtrados.filter((p) => p.estado === estadoFiltro);
    }

    setPedidosFiltrados(filtrados);
    setPanelAbierto(false);
  };

  const handleReset = () => {
    setEstadoFiltro("");
    setPedidosFiltrados(pedidos);
    setPanelAbierto(false);
  };

  const handleEditar = (pedidoId) => {
    const pedido = pedidos.find((p) => p.id === pedidoId);
    if (!pedido) return;
    if (pedido.estado === "Programada" && pedido.envio !== "Pendiente" && pedido.envio !== "Sin Envio") {
      mostraAlertaError(
        "Este pedido ya está programado y no se puede editar por que ya se entrego al proveedor.",
        "Acción no permitida"
      );
      return;
    }

    if (pedido.estado === "Completada") {
      mostraAlertaError(
        "Este pedido ya fue completado y no se puede editar.",
        "Acción no permitida"
      );
      return;
    }

    if (pedido.estado === "Cancelada") {
      mostraAlertaError(
        "Este pedido ya fue cancelado y no se puede editar.",
        "Acción no permitida"
      );
      return;
    }

    if (pedidoSeleccionado === pedidoId) {
      setPedidoSeleccionado(null);
    } else {
      setPedidoSeleccionado(pedidoId);
      setDetallesEdicion(pedido.detalles);
      setDireccionesEdicion(pedido.direcciones || []);
      setNotaNueva(pedido.notas);
    }
  };

  useEffect(() => {
    if (pedidoSeleccionado && refEdicion.current) {
      refEdicion.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [pedidoSeleccionado]);

  const handleCancelar = (pedidoId) => {
    const pedido = pedidos.find((p) => p.id === pedidoId);
    if (!pedido) return;

    if (pedido.estado === "Completada") {
      mostraAlertaError(
        "Este pedido ya fue completado y no se puede cancelar.",
        "Acción no permitida"
      );
      return;
    }

    if (pedido.estado !== "Programada") {
      if (pedido.estado !== "Pendiente") {
        mostraAlertaError(
          "Solo se pueden cancelar pedidos en estado 'Pendiente'.",
          "Acción no permitida"
        );
        return;
      }
    }

    if (pedido.envio === "Enviado") {
      mostraAlertaError(
        "Este pedido ya fue enviado y no se puede cancelar.",
        "Acción no permitida"
      );
      return;
    }

    if (pedido.envio === "Entregado") {
      mostraAlertaError(
        "Este pedido ya fue entregado al proveedor y no se puede cancelar.",
        "Acción no permitida"
      );
      return;
    }

    confirmarAccion(pedidoId, "Cancelar");
  };

  const columnas = useMemo(() => [
    {
      title: "Acciones",
      data: null,
      orderable: false,
      createdCell: (td, cellData, rowData) => {
        const root = createRoot(td);
        root.render(
          <div style={{ display: "flex", justifyContent: "center", gap: "0.5em", flexWrap: "nowrap" }}>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => handleEditar(rowData.id)}
              title="Editar Pedido"
              style={{ minWidth: "35px" }}
              disabled={rowData.estado === "Completada" || rowData.estado === "Cancelada" || (rowData.estado === "Programada" && rowData.envio !== "Pendiente" && rowData.envio !== "Sin Envio")}
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => handleCancelar(rowData.id)}
              title="Cancelar Pedido"
              style={{ minWidth: "35px" }}
              disabled={rowData.estado === "Cancelada" || rowData.estado === "Completada" || rowData.envio === "Enviado" || rowData.envio === "Entregado" || (rowData.estado === "Programada" && rowData.envio !== "Pendiente" && rowData.envio !== "Sin Envio")}
            >
              <i className="fas fa-trash" />
            </button>
          </div>
        );
      }
    },
    {
      title: "Productos",
      data: "detalles",
      render: (data) => {
        if (!data || data.length === 0) {
          return "Sin productos";
        }
        const primerProducto = data[0].nombre;
        if (data.length === 1) {
          return primerProducto;
        }
        const productosRestantes = data.length - 1;
        return `${primerProducto} (+${productosRestantes} más)`;
      }
    },
    {
      title: "Fecha",
      data: "fecha",
      render: (data) => {
        if (!data) return "";
        const date = new Date(data);
        const formattedDate = date.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' });
        const formattedTime = date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: true });
        return `${formattedDate}<br>${formattedTime}`;
      }
    },
    {
      title: "Estado y Envío",
      data: null,
      render: (data, type, rowData) => {
        let estadoBadgeClass = "";
        if (rowData.estado === "Pendiente") estadoBadgeClass = "bg-warning";
        else if (rowData.estado === "Programada") estadoBadgeClass = "bg-success";
        else if (rowData.estado === "Completada") estadoBadgeClass = "bg-primary";
        else if (rowData.estado === "Cancelada") estadoBadgeClass = "bg-danger";

        let envioBadgeClass = "";
        if (rowData.envio === "Pendiente") envioBadgeClass = "bg-warning";
        else if (rowData.envio === "Enviado") envioBadgeClass = "bg-success";
        else if (rowData.envio === "Entregado") envioBadgeClass = "bg-primary";
        else if (rowData.envio === "Sin Envio") envioBadgeClass = "bg-secondary";

        const estadoSpan = `<span class="badge ${estadoBadgeClass}">${rowData.estado}</span>`;
        const envioSpan = `<span class="badge ${envioBadgeClass}">${rowData.envio}</span>`;

        return `${estadoSpan}<br>${envioSpan}`;
      },
    },
    { title: "Total (L)", data: "total" },
  ], [handleEditar, handleCancelar]);

  useEffect(() => {
    fetchPedidosCliente();
  }, [listaProductos]);

  useEffect(() => {
    const productosTransformados = transformarProductos(listaProductos);
    setProductos(productosTransformados);
  }, [listaProductos]);


  const confirmarAccion = async (pedidoId, accion) => {
    const mensaje =
      accion === "Cancelar"
        ? "¿Estás seguro de cancelar este pedido?"
        : "¿Deseas guardar los cambios en este pedido?";

    mostraAlertaPersonalizada({
      titulo: "Confirmación requerida",
      mensaje,
      icono: accion === "Cancelar" ? "warning" : "question",
      textoConfirmacion: accion === "Cancelar" ? "Sí, cancelar" : "Guardar cambios",
      textoCancelacion: "Regresar",
      accion: async (confirmado) => {
        if (confirmado) {
          const tieneDireccion = direccionesEdicion.length > 0;

          if (tieneDireccion && direccionesEdicion.length > 1) {
            return mostraAlertaError("Solo se permite una dirección por pedido.", "Error");
          }

          const envio = tieneDireccion ? "Pendiente" : "Sin envío";

          const payload = {
            fecha: pedidos.find((p) => p.id === pedidoId).fecha,
            estado: accion === "Cancelar" ? "Cancelada" : "Pendiente",
            notas: notaNueva || "Sin notas adicionales",
            clienteId: usuario.datoPersonales.id,
            envio,
            detalles: detallesEdicion.map((detalle) => ({
              cantidad: parseInt(detalle.cantidad, 10),
              precio: parseFloat(detalle.precio),
              productoId: detalle.productoId,
              loteId: detalle.loteId || null
            })),
            direcciones: tieneDireccion
              ? direccionesEdicion.map((dir) => ({
                direccion: dir.direccion,
                latitud: dir.latitud,
                longitud: dir.longitud
              }))
              : []
          };

          try {
            await AxiosPublico.put(`${PedidosEditar}?id=${pedidoId}`, payload);
            mostraAlertaOk(
              accion === "Cancelar"
                ? "Pedido cancelado correctamente."
                : "Pedido actualizado correctamente.",
              "Éxito"
            );
            fetchPedidosCliente();
            setPedidoSeleccionado(null);
            setTimeout(() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }, 100);
          } catch (error) {
            mostraAlertaError("Error al realizar la acción. Inténtalo de nuevo.", "Error");
          }
        }
      },
    });
  };

  const handleCambiarProducto = (index) => {
    setProductoSeleccionado(index);
    setModalProductos(true);
  };

  const seleccionarProducto = (producto) => {
    const nuevosDetalles = [...detallesEdicion];
    nuevosDetalles[productoSeleccionado].productoId = producto.id;
    nuevosDetalles[productoSeleccionado].nombre = producto.nombre;
    nuevosDetalles[productoSeleccionado].productoprecios = producto.productoprecios || [];
    nuevosDetalles[productoSeleccionado].lotes = producto.lotes || [];
    const cantidadActual = nuevosDetalles[productoSeleccionado].cantidad;
    const {
      price: nuevoPrecioCalculado,
      esMayoreoAplicado,
      precioOriginalProducto,
      esPrecioDeLoteBaseAplicado,
      esPrecioDeLoteCantidadAplicado,
      loteId
    } = calcularPrecioFinalConTodo(
      {
        ...producto,
        precio: parseFloat(producto.precio),
        detallepromocions: [],
      },
      cantidadActual
    );

    nuevosDetalles[productoSeleccionado].precio = parseFloat(nuevoPrecioCalculado);
    nuevosDetalles[productoSeleccionado].subtotal = (cantidadActual * parseFloat(nuevoPrecioCalculado)).toFixed(2);
    nuevosDetalles[productoSeleccionado].esMayoreoAplicado = esMayoreoAplicado;
    nuevosDetalles[productoSeleccionado].precioOriginalProducto = precioOriginalProducto;
    nuevosDetalles[productoSeleccionado].esPrecioDeLoteBaseAplicado = esPrecioDeLoteBaseAplicado;
    nuevosDetalles[productoSeleccionado].esPrecioDeLoteCantidadAplicado = esPrecioDeLoteCantidadAplicado;
    nuevosDetalles[productoSeleccionado].loteId = loteId;

    setDetallesEdicion(nuevosDetalles);
    setModalProductos(false);
  };


  const handleEliminarProducto = (index) => {
    const nuevosDetalles = [...detallesEdicion];
    nuevosDetalles.splice(index, 1);
    setDetallesEdicion(nuevosDetalles);
  };

  const agregarAlCarrito = (producto) => {
    if (!producto.id) {
      mostraAlertaError("Error: El producto seleccionado no tiene un ID válido.", "Error");
      return;
    }

    const productoCompleto = listaProductos.find(p => p.id === producto.id);
    if (!productoCompleto) {
      mostraAlertaError("Error: Producto no encontrado en la lista maestra.", "Error");
      return;
    }

    setDetallesEdicion((prev) => {
      const productoExistente = prev.find((item) => item.productoId === producto.id);
      if (productoExistente) {
        const nuevaCantidad = productoExistente.cantidad + 1;
        const {
          price: nuevoPrecioCalculado,
          esMayoreoAplicado,
          precioOriginalProducto,
          esPrecioDeLoteBaseAplicado,
          esPrecioDeLoteCantidadAplicado,
          loteId
        } = calcularPrecioFinalConTodo(
          {
            ...productoCompleto,
            precio: parseFloat(productoCompleto.precio),
            detallepromocions: [],
          },
          nuevaCantidad
        );
        return prev.map((item) =>
          item.productoId === producto.id
            ? {
              ...item,
              cantidad: nuevaCantidad,
              precio: parseFloat(nuevoPrecioCalculado),
              subtotal: (nuevaCantidad * parseFloat(nuevoPrecioCalculado)).toFixed(2),
              esMayoreoAplicado,
              precioOriginalProducto,
              esPrecioDeLoteBaseAplicado,
              esPrecioDeLoteCantidadAplicado,
              loteId
            }
            : item
        );
      } else {
        const {
          price: precioUnitarioInicial,
          esMayoreoAplicado,
          precioOriginalProducto,
          esPrecioDeLoteBaseAplicado,
          esPrecioDeLoteCantidadAplicado,
          loteId
        } = calcularPrecioFinalConTodo(
          {
            ...productoCompleto,
            precio: parseFloat(productoCompleto.precio),
            detallepromocions: [],
          },
          1
        );
        return [
          ...prev,
          {
            productoId: producto.id,
            nombre: producto.nombre,
            cantidad: 1,
            precio: parseFloat(precioUnitarioInicial),
            subtotal: parseFloat(precioUnitarioInicial).toFixed(2),
            productoprecios: productoCompleto.productoprecios || [],
            detallepromocions: [],
            lotes: productoCompleto.lotes || [],
            esMayoreoAplicado,
            precioOriginalProducto,
            esPrecioDeLoteBaseAplicado,
            esPrecioDeLoteCantidadAplicado,
            loteId
          },
        ];
      }
    });

    setModalIsOpen(false);
  };

  const verCatalogo = () => {
    navigate("/app/clientes/productos");
  };

  const filteredProductos = productos.filter((producto) =>
    (producto.nombre + producto.codigo)
      .toLowerCase()
      .includes(productoSearchTerm.toLowerCase())
  );

  const [paginaActual, setPaginaActual] = useState(1);
  const productosPorPagina = 5;
  const indexUltimo = paginaActual * productosPorPagina;
  const indexPrimero = indexUltimo - productosPorPagina;
  const productosPagina = filteredProductos.slice(indexPrimero, indexUltimo);
  const totalPaginas = Math.ceil(filteredProductos.length / productosPorPagina);

  const pedidoActual = pedidos.find(p => p.id === pedidoSeleccionado);
  const envio = pedidoActual?.envio || "";
  const puedeGestionarDireccion =
    envio.trim().toLowerCase() !== "sin envío";

  const totalPedido = useMemo(() => {
    return detallesEdicion.reduce((sum, item) => {
      return sum + (parseFloat(item.precio) * item.cantidad);
    }, 0).toFixed(2);
  }, [detallesEdicion]);


  return (
    <Page datos={datos}>
      <div ref={topRef} />
      <Card>
        <div className="mb-4 d-flex justify-content-end">
          <div className="position-relative">
            <button
              className={`btn ${panelAbierto ? "btn-danger" : "btn-primary"} d-flex justify-content-between align-items-center`}
              onClick={() => setPanelAbierto(!panelAbierto)}
              style={{
                borderRadius: "6px",
                padding: "6px 12px",
                fontSize: "0.9rem",
                minWidth: "140px",
              }}
            >
              <span className="d-flex align-items-center">
                <i className="fas fa-filter mx-2"></i>
                Filtros
              </span>
              <i className={`fas fa-chevron-${panelAbierto ? "up" : "down"}`}></i>
            </button>

            {panelAbierto && (
              <div
                className="position-absolute top-100 mt-2 bg-body border border-secondary-subtle text-body shadow"
                style={{
                  minWidth: "240px",
                  right: 0,
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  padding: "15px",
                  zIndex: 1050,
                  border: "1px solid red",
                }}
              >
                <div className="form-group me-2">
                  <label htmlFor="estadoFiltro">Estado</label>
                  <select
                    id="estadoFiltro"
                    className="form-control form-control-sm"
                    value={estadoFiltro}
                    onChange={(e) => setEstadoFiltro(e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="Pendiente">Pendiente</option>
                    <option value="Programada">Programada</option>
                    <option value="Completada">Completada</option>
                    <option value="Cancelada">Cancelada</option>
                  </select>
                </div>
                <div className="d-flex">
                  <button className="btn btn-sm btn-primary mx-2" onClick={handleFiltrar}>
                    <i className="fas fa-filter mx-1"></i> Filtrar
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={handleReset}>
                    <i className="fas fa-sync-alt mx-1"></i> Reset
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <Tablas datos={pedidosFiltrados} columnas={columnas} id="tablaPedidos" />
      </Card>
      {pedidoSeleccionado && (
        <div ref={refEdicion}>
          <Card titulo="Detalles del pedido" className="p-3 shadow-lg rounded"
            pie={
              <div className="d-grid gap-2 d-md-flex justify-content-md-start">
                <button
                  className="btn btn-success"
                  onClick={() => confirmarAccion(pedidoSeleccionado, "Guardar")}
                >Guardar
                  <i className="fas fa-save me-2 mx-2"></i> 
                </button>
                <ModalBootstrap
                  titulo="Agregar Producto"
                  nombreBoton="Agregar"
                  estiloBoton="btn btn-primary mx-3"
                  iconBoton="fas fa-cart-plus"
                  modalIsOpen={modalIsOpen}
                  setModalIsOpen={setModalIsOpen}
                >
                  <div className="mb-3 d-flex justify-content-center">
                    <div className="col-12 col-md-8 col-lg-6">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar producto por nombre o código..."
                        value={productoSearchTerm}
                        onChange={(e) => setProductoSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-bordered table-striped table-hover shadow-sm">
                      <thead className="bg-dark text-white">
                        <tr>
                          <th>Imagen</th>
                          <th>Detalles del Producto</th>
                          <th>Agregar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productosPagina.length > 0 ? (
                          productosPagina.map((producto) => (
                            <tr key={producto.id}>
                              <td className="text-center align-middle">
                                <img
                                  src={producto.imagen}
                                  alt={producto.nombre}
                                  className="img-thumbnail"
                                  style={{ width: "80px", height: "80px", objectFit: "cover" }}
                                />
                              </td>
                              <td className="align-middle">
                                <div className="d-flex flex-column">
                                  <div className="fw-bold">{producto.nombre}</div>
                                  <small className="text-muted mb-2">{producto.codigo}</small>
                                  {(() => {
                                    const { price, esPrecioDeLoteBaseAplicado, esPrecioDeLoteCantidadAplicado, esMayoreoAplicado } = calcularPrecioFinalConTodo(producto, 1);
                                    const primerLoteActivo = (producto.lotes || []).find(lote => lote.estado === "Disponible" && lote.existencia > 0);
                                    return (
                                      <div>
                                        <span className="text-success fw-bold">{formatoMoneda(price)}</span>
                                        {esPrecioDeLoteCantidadAplicado && (
                                          <span className="badge bg-info mt-1 ms-2">Precio por Cantidad</span>
                                        )}
                                        {esMayoreoAplicado && (
                                          <span className="badge bg-warning mt-1 ms-2">Precio por Mayor</span>
                                        )}
                                        {primerLoteActivo && primerLoteActivo.loteprecios && primerLoteActivo.loteprecios.length > 0 && (
                                          <div className="mt-2">
                                            <p className="small mb-0 text-muted">Precios por Cantidad:</p>
                                            <ul className="list-unstyled small">
                                              {primerLoteActivo.loteprecios
                                                .sort((a, b) => a.cantidad - b.cantidad)
                                                .map((tier, idx) => (
                                                  <li key={idx}>
                                                    {tier.cantidad}+ unid : {formatoMoneda(tier.precio)}
                                                  </li>
                                                ))}
                                            </ul>
                                          </div>
                                        )}
                                        {!esPrecioDeLoteBaseAplicado && !esPrecioDeLoteCantidadAplicado && producto.productoprecios && producto.productoprecios.length > 0 && (
                                          <div className="mt-2">
                                            <p className="small mb-0 text-muted">Precios por Mayor (Producto):</p>
                                            <ul className="list-unstyled small">
                                              {producto.productoprecios
                                                .sort((a, b) => a.cantidad - b.cantidad)
                                                .map((tier, idx) => (
                                                  <li key={idx}>
                                                    {tier.cantidad}+ unid : {formatoMoneda(tier.precio)}
                                                  </li>
                                                ))}
                                            </ul>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </td>
                              <td className="text-center align-middle">
                                <div className="d-flex gap-2">
                                  <button
                                    className="btn btn-success btn-sm mx-2"
                                    onClick={() => agregarAlCarrito(producto)}
                                  >
                                    <i className="fa-solid fa-cart-plus" />
                                  </button>
                                  <button
                                    className="btn btn-info btn-sm d-flex align-items-center text-white"
                                    onClick={verCatalogo}
                                  >
                                    <i className="fa-solid fa-store me-1"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center text-muted">
                              No se encontraron productos.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <nav className="mt-4">
                    <ul className="pagination pagination-sm justify-content-center shadow-sm bg-light p-2 rounded">
                      <li className={`page-item ${paginaActual === 1 ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setPaginaActual(paginaActual - 1)}
                          aria-label="Anterior"
                        >
                          <i className="fas fa-chevron-left me-1"></i> Anterior
                        </button>
                      </li>
                      {[...Array(totalPaginas)].map((_, i) => (
                        <li
                          key={i}
                          className={`page-item ${paginaActual === i + 1 ? "active" : ""}`}
                        >
                          <button className="page-link fw-bold" onClick={() => setPaginaActual(i + 1)}>
                            {i + 1}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${paginaActual === totalPaginas ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setPaginaActual(paginaActual + 1)}
                          aria-label="Siguiente"
                        >
                          Siguiente <i className="fas fa-chevron-right ms-1"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </ModalBootstrap>
              </div>
            }
          >
            <div className="d-flex flex-column gap-3">
              {detallesEdicion.map((detalle, index) => {
                const productoDeLista = listaProductos.find(p => p.id === detalle.productoId);
                const productoParaCalculo = productoDeLista || {
                  ...detalle,
                  precio: detalle.precioOriginalProducto || detalle.precio,
                };
                return (
                  <div className="card shadow-sm" key={index}>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="card-title mb-0 fw-bold">{detalle.nombre}</h5>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-info d-flex align-items-center"
                            onClick={() => handleCambiarProducto(index)}
                          >
                            <i className="fas fa-exchange-alt"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-danger d-flex align-items-center mx-2"
                            onClick={() => handleEliminarProducto(index)}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <p className="mb-0 text-muted">Cantidad:</p>
                          <input
                            type="number"
                            className="form-control form-control-sm text-center w-50"
                            value={detalle.cantidad}
                            min="1"
                            onChange={(e) =>
                              setDetallesEdicion((prev) => {
                                const nuevos = [...prev];
                                const cantidad = parseInt(e.target.value, 10) || 1;
                                nuevos[index].cantidad = cantidad;
                                if (productoParaCalculo) {
                                  const {
                                    price: nuevoPrecioCalculado,
                                    esMayoreoAplicado,
                                    precioOriginalProducto,
                                    esPrecioDeLoteBaseAplicado,
                                    esPrecioDeLoteCantidadAplicado,
                                  } = calcularPrecioFinalConTodo(
                                    {
                                      ...productoParaCalculo,
                                      precio: parseFloat(productoDeLista?.precio || detalle.precioOriginalProducto || detalle.precio),
                                      detallepromocions: [],
                                    },
                                    cantidad
                                  );
                                  nuevos[index].precio = parseFloat(nuevoPrecioCalculado);
                                  nuevos[index].subtotal = (cantidad * parseFloat(nuevoPrecioCalculado)).toFixed(2);
                                  nuevos[index].esMayoreoAplicado = esMayoreoAplicado;
                                  nuevos[index].precioOriginalProducto = precioOriginalProducto;
                                  nuevos[index].esPrecioDeLoteBaseAplicado = esPrecioDeLoteBaseAplicado;
                                  nuevos[index].esPrecioDeLoteCantidadAplicado = esPrecioDeLoteCantidadAplicado;
                                }
                                return nuevos;
                              })
                            }
                          />
                        </div>
                        <div className="text-end d-flex flex-column justify-content-center align-items-end">
                          <p className="mb-0 text-muted">Precio:</p>
                          <div className="d-flex flex-column align-items-end" style={{ minHeight: '38px' }}>
                            <p className="mb-0 text-success fw-bold">{formatoMoneda(detalle.precio)}</p>
                            {detalle.esPrecioDeLoteCantidadAplicado && (
                              <span className="badge bg-info mt-1">Precio por Cantidad</span>
                            )}
                            {detalle.esMayoreoAplicado && !detalle.esPrecioDeLoteCantidadAplicado && (
                              <span className="badge bg-warning mt-1">Precio por Mayor</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-2">
                        <p className="mb-0 fw-bold">Subtotal:</p>
                        <p className="mb-0 text-success fw-bold fs-5">{formatoMoneda(detalle.subtotal)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {puedeGestionarDireccion && (
              <div className="row">
                <div className="col-12">
                  <label>Direcciones</label>
                  <div className="pb-3">
                    <GestionUbicacion
                      direccion={nuevaDireccion}
                      setDireccion={setNuevaDireccion}
                      isModalOpen={isModalOpen}
                      setIsModalOpen={setIsModalOpen}
                      setSeleccionarDireccion={setSeleccionarDireccion}
                      textoBoton={"Agregar dirección"}
                      verdireccion={false}
                      disabled={direccionesEdicion.length >= 1}
                    />
                  </div>
                </div>
              </div>
            )}
            {puedeGestionarDireccion && (
              <div className="row">
                <div className="col-12">
                  <label className="fw-bold">Direcciones del Pedido</label>
                  <div className="form-group">
                    {direccionesEdicion.length > 0 ? (
                      direccionesEdicion.map((f, index) => (
                        <div key={index} className="row align-items-center mb-2">
                          <div className="col-md-8">
                            <input
                              type="text"
                              className="form-control"
                              value={f.direccion}
                              onChange={(e) => {
                                const nuevas = [...direccionesEdicion];
                                nuevas[index].direccion = e.target.value;
                                setDireccionesEdicion(nuevas);
                              }}
                            />
                          </div>
                          <div className="col-md-4 d-flex gap-2">
                            <VerUbicacion direccion={f} />
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={() => {
                                const nuevas = [...direccionesEdicion];
                                nuevas.splice(index, 1);
                                setDireccionesEdicion(nuevas);
                              }}
                              title="Eliminar dirección"
                            >
                              <i className="fa fa-minus" />
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted fst-italic">No hay direcciones agregadas aún.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="mb-3">
              <label className="form-label fw-bold">
                <i className="fas fa-sticky-note me-1"></i> Nota del Pedido:
              </label>
              <textarea
                className="form-control border-primary rounded shadow-sm"
                placeholder="Agregar una nota..."
                value={notaNueva}
                onChange={(e) => setNotaNueva(e.target.value)}
              ></textarea>
            </div>
            <h5 className="me-3 mt-4">Total del Pedido:
              <span className="text-success">{formatoMoneda(totalPedido)}
              </span>
            </h5>
          </Card>
        </div>
      )}
      {modalProductos && (
        <ModalBootstrap
          titulo="Seleccionar Producto"
          nombreBoton="Cerrar"
          estiloBoton="btn-secondary"
          modalIsOpen={modalProductos}
          setModalIsOpen={setModalProductos}
        >
          <div className="mb-3 d-flex">
            <div className="col-md-6 col-sm-8">
              <input
                type="text"
                className="form-control"
                placeholder="Buscar producto por nombre o código..."
                value={productoSearchTerm}
                onChange={(e) => setProductoSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-bordered table-striped table-hover shadow-sm">
              <thead className="bg-dark text-white">
                <tr>
                  <th>Imagen</th>
                  <th>Detalles del Producto</th>
                  <th>Agregar</th>
                </tr>
              </thead>
              <tbody>
                {productosPagina.length > 0 ? (
                  productosPagina.map((producto) => (
                    <tr key={producto.id}>
                      <td className="text-center align-middle">
                        <img
                          src={producto.imagen}
                          alt={producto.nombre}
                          className="img-thumbnail"
                          style={{ width: "80px", height: "80px", objectFit: "cover" }}
                        />
                      </td>
                      <td className="align-middle">
                        <div className="d-flex flex-column">
                          <div className="fw-bold">{producto.nombre}</div>
                          <small className="text-muted mb-2">{producto.codigo}</small>
                          {(() => {
                            const { price, esPrecioDeLoteBaseAplicado, esPrecioDeLoteCantidadAplicado, esMayoreoAplicado } = calcularPrecioFinalConTodo(producto, 1);
                            const primerLoteActivo = (producto.lotes || []).find(lote => lote.estado === "Disponible" && lote.existencia > 0);
                            return (
                              <div>
                                <span className="text-success fw-bold">{formatoMoneda(price)}</span>
                                {esPrecioDeLoteCantidadAplicado && (
                                  <span className="badge bg-info mt-1 ms-2">Precio por Cantidad</span>
                                )}
                                {esMayoreoAplicado && (
                                  <span className="badge bg-warning mt-1 ms-2">Precio por Mayor</span>
                                )}
                                {primerLoteActivo && primerLoteActivo.loteprecios && primerLoteActivo.loteprecios.length > 0 && (
                                  <div className="mt-2">
                                    <p className="small mb-0 text-muted">Precios por Cantidad (Lote):</p>
                                    <ul className="list-unstyled small">
                                      {primerLoteActivo.loteprecios
                                        .sort((a, b) => a.cantidad - b.cantidad)
                                        .map((tier, idx) => (
                                          <li key={idx}>
                                            {tier.cantidad}+ unid : {formatoMoneda(tier.precio)}
                                          </li>
                                        ))}
                                    </ul>
                                  </div>
                                )}
                                {!esPrecioDeLoteBaseAplicado && !esPrecioDeLoteCantidadAplicado && producto.productoprecios && producto.productoprecios.length > 0 && (
                                  <div className="mt-2">
                                    <p className="small mb-0 text-muted">Precios por Mayor (Producto):</p>
                                    <ul className="list-unstyled small">
                                      {producto.productoprecios
                                        .sort((a, b) => a.cantidad - b.cantidad)
                                        .map((tier, idx) => (
                                          <li key={idx}>
                                            {tier.cantidad}+ unid : {formatoMoneda(tier.precio)}
                                          </li>
                                        ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="text-center align-middle">
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-success btn-sm mx-2"
                            onClick={() => seleccionarProducto(producto)}
                          >
                            <i className="fa-solid fa-cart-plus" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center text-muted">
                      No se encontraron productos.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <nav className="mt-4">
            <ul className="pagination pagination-sm justify-content-center shadow-sm bg-light p-2 rounded">
              <li className={`page-item ${paginaActual === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setPaginaActual(paginaActual - 1)}
                  aria-label="Anterior"
                >
                  <i className="fas fa-chevron-left me-1"></i> Anterior
                </button>
              </li>
              {[...Array(totalPaginas)].map((_, i) => (
                <li
                  key={i}
                  className={`page-item ${paginaActual === i + 1 ? "active" : ""}`}
                >
                  <button className="page-link fw-bold" onClick={() => setPaginaActual(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${paginaActual === totalPaginas ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setPaginaActual(paginaActual + 1)}
                  aria-label="Siguiente"
                >
                  Siguiente <i className="fas fa-chevron-right ms-1"></i>
                </button>
              </li>
            </ul>
          </nav>
        </ModalBootstrap>
      )}
    </Page>
  );
};
export default EditarPedidos;